import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import PFD from "./PFD.jsx";
import NavDisplay from "./NavDisplay.jsx";
import EngineDisplay from "./EngineDisplay.jsx";
import SteamGauges from "./SteamGauges.jsx";
import { getCockpitProfile } from "./profiles.js";
import { metresToFeet, msToKnots, prettyCallsign } from "../lib/format.js";
import "./cockpit.css";

// The WebGL out-the-window scene pulls in Three.js — lazy-load it so that heavy
// dependency only downloads when someone actually opens the cockpit.
const OutTheWindow = lazy(() => import("./OutTheWindow.jsx"));

// Estimate bank angle from the rate of heading change (a coordinated turn:
// tan(bank) = ω·V/g). We keep a short history of (t, heading) samples.
function useBankAngle(aircraft) {
  const [bank, setBank] = useState(0);
  const last = useRef(null);
  useEffect(() => {
    if (!aircraft || aircraft.heading == null || !aircraft.velocity) {
      last.current = null;
      setBank(0);
      return;
    }
    const now = performance.now();
    const prev = last.current;
    last.current = { t: now, hdg: aircraft.heading };
    if (!prev) return;
    let dHdg = aircraft.heading - prev.hdg;
    while (dHdg > 180) dHdg -= 360;
    while (dHdg < -180) dHdg += 360;
    const dt = (now - prev.t) / 1000;
    if (dt <= 0 || dt > 30) return; // ignore stale gaps
    const omega = (dHdg * Math.PI) / 180 / dt; // rad/s
    const g = 9.81;
    const raw = (Math.atan((omega * aircraft.velocity) / g) * 180) / Math.PI;
    const clamped = Math.max(-30, Math.min(30, raw));
    // Smooth so it doesn't jump between refreshes.
    setBank((b) => b * 0.6 + clamped * 0.4);
  }, [aircraft?.heading, aircraft?.velocity]); // eslint-disable-line react-hooks/exhaustive-deps
  return bank;
}

function FcuValue({ label, value, unit }) {
  return (
    <div className="fcu-cell">
      <span className="fcu-label">{label}</span>
      <span className="fcu-value">
        {value}
        {unit ? <span className="fcu-unit"> {unit}</span> : null}
      </span>
    </div>
  );
}

export default function CockpitView({ aircraft, details, traffic = [], onExit }) {
  const bank = useBankAngle(aircraft);

  // Press Escape to leave the cockpit.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onExit?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onExit]);

  // Resolve the aircraft type (enrichment is more reliable than the ADS-B tag)
  // and derive a per-type flight-deck profile: family, colours, engine count.
  const typeCode = details?.aircraft?.typeCode || aircraft?.type || null;
  const profile = useMemo(
    () =>
      getCockpitProfile({
        typeCode,
        manufacturer: details?.aircraft?.manufacturer,
        model: details?.aircraft?.model,
      }),
    [typeCode, details?.aircraft?.manufacturer, details?.aircraft?.model]
  );

  // Give the instruments the resolved type so engine count etc. are accurate.
  const acForInstruments = useMemo(
    () => (aircraft ? { ...aircraft, type: typeCode || aircraft.type } : null),
    [aircraft, typeCode]
  );

  const cockpitStyle = {
    "--ck-accent": profile.accent,
    "--ck-bezel": profile.bezel,
    "--ck-glare": profile.glare,
    "--ck-panel-a": profile.panelA,
    "--ck-panel-b": profile.panelB,
  };

  const alt = aircraft ? metresToFeet(aircraft.baroAltitude) : null;
  const spd = aircraft ? msToKnots(aircraft.velocity) : null;
  const hdg = aircraft?.heading != null ? Math.round(aircraft.heading) : null;
  const vs = aircraft?.verticalRate != null ? Math.round(aircraft.verticalRate * 196.85) : null;

  return (
    <div
      className={`cockpit cockpit--${profile.family}`}
      style={cockpitStyle}
      role="group"
      aria-label={`Cockpit instrument view — ${profile.label}`}
    >
      {/* Windshield — a live WebGL view of what the pilot sees outside, framed by
          cockpit window posts. Reflects altitude, heading, pitch and bank. */}
      <div className="windshield">
        <Suspense fallback={<div className="otw-loading">Loading the view outside…</div>}>
          <OutTheWindow aircraft={acForInstruments} bankDeg={bank} />
        </Suspense>
        <div className="window-frame" aria-hidden="true">
          <span className="post post-l" />
          <span className="post post-c" />
          <span className="post post-r" />
        </div>
      </div>

      {/* Glareshield + FCU (autoflight) strip along the top */}
      <div className="glareshield">
        <div className="fcu">
          <FcuValue label="SPD" value={spd != null ? spd : "---"} unit="kt" />
          <FcuValue label="HDG" value={hdg != null ? String(hdg).padStart(3, "0") : "---"} unit="°" />
          <FcuValue label="ALT" value={alt != null ? alt.toLocaleString() : "-----"} unit="ft" />
          <FcuValue
            label="V/S"
            value={vs != null ? (vs > 0 ? `+${vs}` : String(vs)) : "----"}
            unit="fpm"
          />
          <div className="fcu-ac">
            {aircraft ? prettyCallsign(aircraft.callsign) : "NO AIRCRAFT SELECTED"}
            <span className="fcu-type">
              {profile.tag} {profile.model || typeCode || "Unknown type"} · {profile.engines}× eng
            </span>
          </div>
        </div>
        <button className="cockpit-exit" onClick={onExit} aria-label="Exit cockpit view">
          ✕ Exit
        </button>
      </div>

      {!aircraft && (
        <p className="cockpit-hint">
          Select an aircraft on the map (or search for one) to fly its instruments.
        </p>
      )}

      {/* Instrument panel. Light GA aircraft get the analog "steam gauge" panel;
          airliners / regional jets get the glass PFD · ND · EWD. Instruments get
          the resolved-type aircraft so engine count etc. match the real type. */}
      {profile.panelType === "analog" ? (
        <div className="panel panel--analog">
          <SteamGauges aircraft={acForInstruments} bankDeg={bank} />
        </div>
      ) : (
        <div className="panel">
          <div className="screen-bezel pfd">
            <PFD aircraft={acForInstruments} bankDeg={bank} />
          </div>
          <div className="screen-bezel nd">
            <NavDisplay aircraft={acForInstruments} traffic={traffic} />
          </div>
          <div className="screen-bezel ewd">
            <EngineDisplay aircraft={acForInstruments} />
          </div>
        </div>
      )}
    </div>
  );
}
